import axios from 'axios';
import { getOneRosterFileApiBaseUrl } from './constants';
import { NotificationTypes, showNotification } from '../components/Notifications';
import apiClient from './apiClient';
import { SchoolData, OneRosterSchoolsResponse } from './types';
import { BATCH_SIZE } from './constants';

/**
 * Fetches schools from the uploaded OneRoster file
 */
export const fetchOneRosterSchools = async (oneRosterFilePath?: string): Promise<SchoolData[]> => {
  try {
    let dataJsonFile = oneRosterFilePath;
    if (!oneRosterFilePath) {
      const res = await axios.get(`${getOneRosterFileApiBaseUrl()}/oneroster/job/latest/status`, {
        withCredentials: true,
      });

      if (res.data.status === 'COMPLETED') {
        dataJsonFile = res.data.presignedUrl;
      }
    }
    if (dataJsonFile) {
      // Fetch the data from the provided URL
      const response = await axios.get<OneRosterSchoolsResponse>(dataJsonFile);
      const { uniqueSchools } = response.data;
      const updatedSchoolMap = uniqueSchools.map((school) => ({
        ...school,
        school_number: school.id,
        dcid: school.id,
        abbreviation: '',
      }));
      return updatedSchoolMap;
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error fetching OneRoster schools:', error);
    showNotification(NotificationTypes.error, 'Error Getting School Map', 'Failure in getting data from server.');
    throw error;
  }
};

/**
 * Saves the school mapping configuration to the server
 */
export const saveSchoolMapping = async (
  schoolMapping: any,
  districtId: string,
  sisSchools: any[],
): Promise<void> => {
  try {
    const data = Object.keys(schoolMapping).reduce((acc, nid) => {
      const { sisId } = schoolMapping[nid];
      sisId.forEach((id) => {
        acc.push({ sisId: id, nid });
      });
      return acc;
    }, []);

    // Add district mapping for certain integrations
    const sisDistrictRemainingSchools = sisSchools.filter(school => school.type === 'district' && !data.find(schoolEntry => schoolEntry.sisId === school.dcid));
    if (sisDistrictRemainingSchools.length) {
      sisDistrictRemainingSchools.forEach(school => {
        data.push({ sisId: school.dcid, nid: districtId });
      });
    }

    // Process in batches to handle large datasets
    const totalMappings = data.length;
    const batches = Array.from({ length: Math.ceil(totalMappings / BATCH_SIZE) }, (v, i) =>
      data.slice(i * BATCH_SIZE, i * BATCH_SIZE + BATCH_SIZE),
    );

    // Send each batch to the server
    for (let i = 0; i < batches.length; i++) {
      await apiClient.patch('/data-ingest/sis/parameter-group/schoolMappingBatches', {
        schoolMapping: batches[i],
        batchNumber: i,
      });
    }

    showNotification(NotificationTypes.success, 'Success', 'Mapping updated successfully');
  } catch (error) {
    console.error('Error saving school mapping:', error);
    showNotification(NotificationTypes.error, 'Error Saving School Mappings', 'Failure in saving data on server.');
    throw error;
  }
};